import { useState, useEffect } from "react";
import ClientPrivateLayout from "@/layouts/ClientPrivateLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Calendar, Clock, Euro, MapPin, Video } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { isBefore, differenceInHours } from 'date-fns';
import { getRequestOptions, getUserId, formatDateTime, formatDate, formatTime, getCurrencyIcon } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const ClientDashboard = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [bookedClasses, setBookedClasses] = useState<any[]>([]);
  const studioURL = localStorage.getItem("studioURL");

  const handleCancelBooking = (bookingId: string, classId: string) => {
    setSelectedBookingId(bookingId);
    setSelectedClassId(classId);
    setIsConfirmOpen(true);
  };

  const confirmCancellation = async () => {
    try {
      let fetchURL = `${import.meta.env.VITE_BOOKINGS_ENDPOINT}` + '/cancel';
      let id = selectedBookingId;
      let classId = selectedClassId;
      const payload = { id, classId };
      const response = await fetch(fetchURL, getRequestOptions("POST", JSON.stringify(payload), "json"));
      if (!response.ok) {
        throw new Error("Failed to add or update the booking");
      }

      const data = await response.json();
      console.log("Response Data:", data); // Optional: Check response from API
      toast({
        title: t('clientDashboard.cancelSuccess.title'),
        description: t('clientDashboard.cancelSuccess.description'),
        duration: 5000,
        className: "bg-green-500 text-white border-green-600",
      });
      fetchClasses();
    } catch (error) {
      console.error("Error adding class:", error);
    }
    setIsConfirmOpen(false);
    setSelectedBookingId(null);
  };

  // const isClassInPast = (date: string, time: string) => {
  //   const classDateTime = new Date(`${date} ${time}`);
  //   return classDateTime < new Date();
  // };

  // const canCancelBooking = (date: string, time: string) => {
  //   if (isClassInPast(date, time)) return false;

  //   const classDateTime = new Date(`${date} ${time}`);
  //   const now = new Date();
  //   const hoursDifference = (classDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  //   return hoursDifference >= 24;
  // };

  const isClassInPast = (dateTime: string) => {
    return isBefore(formatDateTime(dateTime), new Date()); // Check if classDateTime is before the current time
  };

  const canCancelBooking = (dateTime: string) => {
    if (isClassInPast(dateTime)) return false;

    const hoursDifference = differenceInHours(formatDateTime(dateTime), new Date()); // Get the difference in hours

    return hoursDifference >= 24; // Check if the difference is greater than or equal to 24 hours
  };

  const fetchClasses = async () => {

    try {
      const response = await fetch(`${import.meta.env.VITE_CLIENT_CLASSES_ENDPOINT}` + "/" + getUserId(), getRequestOptions());

      if (!response.ok) {
        throw new Error('Failed to fetch classes');
      }

      const bookedClasses = await response.json(); // Parse JSON data
      setBookedClasses(bookedClasses);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  useEffect(() => {
    document.title = `${t('clientDashboard.title')} - FitCalendar`;
    fetchClasses();
  }, []);

  return (
    <ClientPrivateLayout>
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">{t('clientDashboard.title')}</h1>
          {studioURL && (
            <Link to={`/studio/${studioURL}`}>
              <Button variant="outline">{t('common.backToClasses')}</Button>
            </Link>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('clientDashboard.bookedClasses')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.class')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('common.time')}</TableHead>
                  <TableHead>{t('common.duration')}</TableHead>
                  <TableHead>{t('common.trainer')}</TableHead>
                  <TableHead>{t('common.price')}</TableHead>
                  <TableHead>{t('common.location')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookedClasses.map((bookingClass) => (
                  <TableRow key={bookingClass.id}>
                    <TableCell>{bookingClass.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4" />
                        {formatDate(bookingClass.classDateTime)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        {formatTime(bookingClass.classDateTime)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {bookingClass.duration} {t('common.minutes')}
                      </div>
                    </TableCell>
                    <TableCell>{bookingClass.instructor}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {getCurrencyIcon({ currencyCode: bookingClass.currency })}
                        {bookingClass.price}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {bookingClass.location === "Online" ? (
                          <>
                            <Video className="mr-2 h-4 w-4 text-red-800" />
                            <span className="px-2 py-1 rounded-full text-sm bg-red-100 text-red-800">
                              {t('classes.online')}
                            </span>
                          </>
                        ) : (
                          <>
                            <MapPin className="mr-2 h-4 w-4 text-blue-800" />
                            <span className="px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                              {bookingClass.location}
                            </span>
                          </>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isClassInPast(bookingClass.classDateTime)
                        ? t('common.completed')
                        : t(`common.${bookingClass.bookings[0].status.trim().toLowerCase()}`)}
                    </TableCell>
                    <TableCell>
                      {bookingClass.bookings[0].status.trim().toLowerCase() === "booked" && !isClassInPast(bookingClass.classDateTime) && (
                        canCancelBooking(bookingClass.classDateTime) ? (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleCancelBooking(bookingClass.bookings[0].id, bookingClass.id)}
                          >
                            {t('common.cancel')}
                          </Button>
                        ) : (
                          <span className="text-red-500 text-sm">
                            {t('clientDashboard.cannotCancel')}
                          </span>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('clientDashboard.cancelDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('clientDashboard.cancelDialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.keepBooking')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancellation}>
              {t('common.confirmCancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClientPrivateLayout>
  );
};

export default ClientDashboard;
